import { lazy } from "react";
import { Route, Redirect } from "wouter";
import type { RouteHelpers } from "./types";

const Founders = lazy(() => import("@/pages/Founders"));
const Challenges = lazy(() => import("@/pages/Challenges"));
const ChallengeDetail = lazy(() => import("@/pages/ChallengeDetail"));
const Academy = lazy(() => import("@/pages/Academy"));
const CourseView = lazy(() => import("@/pages/CourseView"));
const Circles = lazy(() => import("@/pages/Circles"));
const CircleDetail = lazy(() => import("@/pages/CircleDetail"));
const Networking = lazy(() => import("@/pages/Networking"));
const EwitaChat = lazy(() => import("@/pages/EwitaChat"));
const NetworkLanding = lazy(() => import("@/pages/network/NetworkLanding"));
const ProgramsLanding = lazy(() => import("@/pages/programs/ProgramsLanding"));
const OfficeHours = lazy(() => import("@/pages/network/OfficeHours"));
const Perks = lazy(() => import("@/pages/network/Perks"));
const IntroRequests = lazy(() => import("@/pages/network/IntroRequests"));
const InvestorNetwork = lazy(() => import("@/pages/network/InvestorNetwork"));
const ClubDetail = lazy(() => import("@/pages/network/ClubDetail"));
const JobPostingWizard = lazy(() => import("@/pages/jobs/JobPostingWizard"));
const JobsPage = lazy(() => import("@/pages/jobs/JobsPage"));
const JobDetailPage = lazy(() => import("@/pages/jobs/JobDetailPage"));
const MyApplicationsPage = lazy(() => import("@/pages/jobs/MyApplicationsPage"));
const ExecutiveSearch = lazy(() => import("@/pages/ExecutiveSearch"));
const ClientPortal = lazy(() => import("@/pages/exec-search/ClientPortal"));
const TalentPortal = lazy(() => import("@/pages/exec-search/TalentPortal"));
const TalentProfile = lazy(() => import("@/pages/exec-search/TalentProfile"));
const MandateIntake = lazy(() => import("@/pages/exec-search/MandateIntake"));
const MandateMatches = lazy(() => import("@/pages/exec-search/MandateMatches"));
const CandidateProfileView = lazy(() => import("@/pages/exec-search/CandidateProfileView"));
const RevealRequests = lazy(() => import("@/pages/exec-search/RevealRequests"));
const SearchRoom = lazy(() => import("@/pages/exec-search/SearchRoom"));
const ChiefInTechSummit = lazy(() => import("@/pages/events/ChiefInTechSummit"));
const ExecutiveForum = lazy(() => import("@/pages/events/ExecutiveForum"));
const ExecutiveForumApply = lazy(() => import("@/pages/events/ExecutiveForumApply"));
const ExecutiveForumCity = lazy(() => import("@/pages/events/ExecutiveForumCity"));
const EventsListing = lazy(() => import("@/pages/events/EventsListing"));
const EventLandingPage = lazy(() => import("@/pages/events/EventLandingPage"));
const EventLandingApply = lazy(() => import("@/pages/events/EventLandingApply"));
const Summit = lazy(() => import("@/pages/Summit"));
const SummitShop = lazy(() => import("@/pages/summit/Shop"));
const FounderProfiles = lazy(() => import("@/pages/FounderProfiles"));

export function NetworkRoutes({ LazyRoute }: RouteHelpers) {
    return (
        <>
            {/* Network hub */}
            <Route path="/network/office-hours">{() => <LazyRoute component={OfficeHours} admin />}</Route>
            <Route path="/network/perks">{() => <LazyRoute component={Perks} />}</Route>
            <Route path="/network/intro-requests">{() => <LazyRoute component={IntroRequests} />}</Route>
            <Route path="/network">{() => <LazyRoute component={NetworkLanding} />}</Route>
            <Route path="/programs">{() => <LazyRoute component={ProgramsLanding} />}</Route>
            {/* Investor network / clubs */}
            <Route path="/investor-network/:slug">{() => <LazyRoute component={ClubDetail} />}</Route>
            <Route path="/investor-network">{() => <LazyRoute component={InvestorNetwork} />}</Route>
            {/* Circles */}
            <Route path="/circles/:id">{() => <LazyRoute component={CircleDetail} />}</Route>
            <Route path="/circles">{() => <LazyRoute component={Circles} />}</Route>
            {/* Networking / Ewita */}
            <Route path="/networking">{() => <LazyRoute component={Networking} />}</Route>
            <Route path="/ewita-chat">{() => <LazyRoute component={EwitaChat} />}</Route>
            {/* Challenges / Academy */}
            <Route path="/challenges/:id">{() => <LazyRoute component={ChallengeDetail} />}</Route>
            <Route path="/challenges">{() => <LazyRoute component={Challenges} />}</Route>
            <Route path="/academy/:id">{() => <LazyRoute component={CourseView} />}</Route>
            <Route path="/academy">{() => <LazyRoute component={Academy} />}</Route>
            {/* Jobs */}
            <Route path="/jobs/post">{() => <LazyRoute component={JobPostingWizard} />}</Route>
            <Route path="/jobs/executive">{() => <LazyRoute component={JobsPage} />}</Route>
            <Route path="/jobs/startups">{() => <LazyRoute component={JobsPage} />}</Route>
            <Route path="/jobs/:slug">{() => <LazyRoute component={JobDetailPage} />}</Route>
            <Route path="/jobs">{() => <LazyRoute component={JobsPage} />}</Route>
            <Route path="/my-applications">{() => <LazyRoute component={MyApplicationsPage} />}</Route>
            {/* Executive search */}
            <Route path="/executive-search/client/new">{() => <LazyRoute component={MandateIntake} />}</Route>
            <Route path="/executive-search/client/mandate/:id/matches">{() => <LazyRoute component={MandateMatches} />}</Route>
            <Route path="/executive-search/client/mandate/:id">{() => <LazyRoute component={SearchRoom} />}</Route>
            <Route path="/executive-search/client">{() => <LazyRoute component={ClientPortal} />}</Route>
            <Route path="/executive-search/talent/profile">{() => <LazyRoute component={TalentProfile} />}</Route>
            <Route path="/executive-search/talent/reveal-requests">{() => <LazyRoute component={RevealRequests} />}</Route>
            <Route path="/executive-search/talent">{() => <LazyRoute component={TalentPortal} />}</Route>
            <Route path="/executive-search/candidate/:id">{() => <LazyRoute component={CandidateProfileView} />}</Route>
            <Route path="/executive-search">{() => <LazyRoute component={ExecutiveSearch} />}</Route>
            {/* Events */}
            <Route path="/events/chief-in-tech-summit">{() => <LazyRoute component={ChiefInTechSummit} />}</Route>
            <Route path="/events/executive-forum/apply">{() => <LazyRoute component={ExecutiveForumApply} />}</Route>
            <Route path="/events/executive-forum">{() => <LazyRoute component={ExecutiveForum} />}</Route>
            <Route path="/events/executive-dinner">{() => <Redirect to="/events/executive-forum" />}</Route>
            {/* Apply-to-join routes must precede the :slug landing routes below */}
            <Route path="/events/forum/:slug/apply">{() => <LazyRoute component={EventLandingApply} />}</Route>
            <Route path="/events/salon/:slug/apply">{() => <LazyRoute component={EventLandingApply} />}</Route>
            <Route path="/events/forum/:slug">{() => <LazyRoute component={EventLandingPage} />}</Route>
            <Route path="/events/salon/:slug">{() => <LazyRoute component={EventLandingPage} />}</Route>
            {/* Redirect old Davos forum URL to Chief in AI Salon */}
            <Route path="/events/forum/forum-world-economic-forum-wef-davos-2027">{() => <Redirect to="/events/salon/chief-in-ai-salon-davos-2027" />}</Route>
            {/* Per-city Executive Forum pages (e.g. /events/executive-forum-nyc). Catch-all —
                must stay below the specific /events/* routes above. Non-forum slugs 404 in-component. */}
            <Route path="/events/:slug">{() => <LazyRoute component={ExecutiveForumCity} />}</Route>
            <Route path="/events">{() => <LazyRoute component={EventsListing} />}</Route>
            <Route path="/summit/shop">{() => <LazyRoute component={SummitShop} />}</Route>
            <Route path="/summit">{() => <Redirect to="/events/chief-in-tech-summit" />}</Route>
            <Route path="/executive-dinner">{() => <Redirect to="/events/executive-forum" />}</Route>
        </>
    );
}
